import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import { bookingTotal } from '@flyingmuse/shared';
import type { Experience } from '../../data/experiences';
import { useExperiences } from '../../hooks';
import {
  createBooking,
  getAvailability,
  getExperienceOptions,
  startCardPayment,
} from '../../lib/booking';
import { rememberCardCheckout } from '../../lib/cardCheckout';
import {
  trackAddContactInfo,
  trackAddPaymentInfo,
  trackBeginCheckout,
  trackBookingAbandon,
  trackBookingError,
  trackCardCheckoutStart,
  trackPurchase,
  trackSelectDate,
  trackSelectExperience,
  trackSelectOption,
  trackSelectPaymentMethod,
} from '../../lib/analytics';
import {
  ENABLED_PAYMENT_METHODS,
  flowReducer,
  guestsValid,
  initialFlowState,
  monthSlots,
  monthSlotsStatus,
  selectedOption,
  selectedSlot,
  type FlowState,
  type Step,
} from './bookingFlow';
import { formatPrice, monthRange, seasonMonths } from './format';
import { WizardSteps } from './WizardSteps';
import { WhatsAppFallback } from './WhatsAppFallback';
import { ExperienceStep } from './steps/ExperienceStep';
import { OptionStep } from './steps/OptionStep';
import { DateGuestsStep } from './steps/DateGuestsStep';
import { ContactStep } from './steps/ContactStep';
import { ReviewStep } from './steps/ReviewStep';
import { SuccessStep } from './steps/SuccessStep';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function humanError(err: unknown): string {
  const msg = (err as { message?: string })?.message;
  if (msg && !/internal/i.test(msg)) return msg;
  return 'Something went wrong completing your booking. Please try again.';
}

function initState(initialExperience?: Experience): FlowState {
  return {
    ...initialFlowState,
    experience: initialExperience ?? null,
    step: initialExperience ? 'option' : 'experience',
  };
}

export function BookingModal({
  initialExperience,
  initialOptionId,
  initialOptionAlias,
  onClose,
}: {
  initialExperience?: Experience;
  initialOptionId?: number;
  initialOptionAlias?: string;
  onClose: () => void;
}) {
  const { byAlias, status: expStatus } = useExperiences();
  const [state, dispatch] = useReducer(flowReducer, initialExperience, initState);

  // The experience step is only reachable when no experience was preselected.
  const canPickExperience = initialExperience == null;

  const product = state.experience
    ? byAlias(state.experience.productAlias)
    : undefined;
  const productId = product?.id ?? null;

  const optionsReqRef = useRef<number | null>(null);
  // Keys are `${productId}:${optionId}:${month}` — one fetch per month, per option.
  const slotsReqRef = useRef<Set<string>>(new Set());

  // Close the wizard, logging an abandon when it had not reached success.
  const handleClose = useCallback(() => {
    if (state.step !== 'success') {
      trackBookingAbandon(state.step, state.experience ?? undefined);
    }
    onClose();
  }, [state.step, state.experience, onClose]);

  // Lock background scroll + close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [handleClose]);

  // Load options once per product (StrictMode-safe via the request ref).
  useEffect(() => {
    if (productId == null || optionsReqRef.current === productId) return;
    optionsReqRef.current = productId;
    dispatch({ type: 'OPTIONS_LOADING' });
    getExperienceOptions(productId)
      .then((res) => {
        // Resolve a preselected package: an explicit id wins, else match by alias.
        const resolvedId =
          initialOptionId ??
          (initialOptionAlias
            ? res.options.find((o) => o.alias === initialOptionAlias)?.id
            : undefined);
        dispatch({
          type: 'OPTIONS_READY',
          options: res.options,
          initialOptionId: resolvedId,
        });
      })
      .catch(() => dispatch({ type: 'OPTIONS_ERROR' }));
  }, [productId, initialOptionId, initialOptionAlias]);

  // Load availability for whichever month is on screen, once each. LinkTwist
  // rejects any window wider than 31 days, so a month is the unit of fetching
  // as well as of browsing; later months load when the guest pages to them.
  useEffect(() => {
    const optionId = state.selectedOptionId;
    const month = state.selectedMonth;
    if (productId == null || optionId == null || !month) return;

    const key = `${productId}:${optionId}:${month}`;
    if (slotsReqRef.current.has(key)) return;
    slotsReqRef.current.add(key);

    dispatch({ type: 'SLOTS_LOADING', month });
    const { from, to } = monthRange(month, new Date());
    getAvailability(productId, optionId, from, to)
      .then((res) => dispatch({ type: 'SLOTS_READY', month, slots: res.slots }))
      .catch(() => {
        // Drop the key so paging back to this month retries rather than
        // sticking on a stale error.
        slotsReqRef.current.delete(key);
        dispatch({ type: 'SLOTS_ERROR', month });
      });
  }, [productId, state.selectedOptionId, state.selectedMonth]);

  const option = selectedOption(state);
  const slot = selectedSlot(state);
  const expLabel = state.experience?.pageTitle;
  // This month through the end of the season (31 October).
  const months = useMemo(() => seasonMonths(new Date()), []);

  const canProceedDate =
    !!slot && !!option && guestsValid(option, state.quantities);
  const contactValid =
    state.contact.fullName.trim().length > 1 &&
    EMAIL_RE.test(state.contact.email.trim()) &&
    state.contact.phone.trim().length >= 3;

  async function handleConfirm() {
    if (!option || !slot || productId == null) return;
    const participants = option.participantTypes
      .map((pt) => ({ alias: pt.alias, quantity: state.quantities[pt.alias] ?? 0 }))
      .filter((p) => p.quantity > 0);
    const trimmedComments = state.comments.trim();
    const input = {
      productId,
      optionId: option.id,
      activityDateTime: slot.dateTime,
      participants,
      contact: {
        fullName: state.contact.fullName.trim(),
        email: state.contact.email.trim(),
        phone: state.contact.phone.trim(),
      },
      ...(trimmedComments ? { comments: trimmedComments } : {}),
      tourLanguage: 'EN',
    };

    // The guest has committed to a settlement method — GA4's checkout step.
    if (state.experience) {
      trackAddPaymentInfo(
        state.experience,
        option,
        slot,
        state.quantities,
        state.paymentMethod,
      );
    }

    dispatch({ type: 'SUBMIT_START' });
    try {
      if (state.paymentMethod === 'card') {
        const payment = await startCardPayment(input);
        // Stash what the return page needs before we leave the site — the
        // wizard's state does not survive the round-trip to Viva.
        rememberCardCheckout({
          orderCode: payment.orderCode,
          paymentId: payment.paymentId,
          bookingCode: payment.bookingCode,
          amount: payment.amount,
          email: input.contact.email,
          experienceAlias: state.experience?.productAlias ?? null,
          experienceTitle: state.experience?.pageTitle ?? null,
          optionTitle: option.title,
          activityDateTime: slot.dateTime,
          guests: participants.reduce((n, p) => n + p.quantity, 0),
          startedAt: Date.now(),
        });
        if (state.experience) {
          trackCardCheckoutStart(
            state.experience,
            option,
            slot,
            state.quantities,
            payment.orderCode,
          );
        }
        // Leave `submitting` set: the modal stays disabled until navigation.
        window.location.assign(payment.checkoutUrl);
        return;
      }

      const result = await createBooking(input);
      if (state.experience) {
        trackPurchase(
          state.experience,
          option,
          slot,
          state.quantities,
          result.bookingCode,
          'arrival',
        );
      }
      dispatch({ type: 'SUBMIT_SUCCESS', result });
    } catch (err) {
      const message = humanError(err);
      trackBookingError(
        state.paymentMethod === 'card' ? 'card_checkout' : 'review',
        message,
        state.experience ?? undefined,
      );
      dispatch({ type: 'SUBMIT_ERROR', error: message });
    }
  }

  /* ---- Loading / unavailable states (after an experience is chosen) ---- */
  const hasExp = !!state.experience;
  const productUnavailable =
    hasExp &&
    productId == null &&
    (expStatus === 'ready' || expStatus === 'error');
  const resolvingProduct =
    hasExp &&
    productId == null &&
    (expStatus === 'idle' || expStatus === 'loading');
  const loadingOptions =
    productId != null &&
    (state.optionsStatus === 'idle' || state.optionsStatus === 'loading');

  /* ---- Body --------------------------------------------------------- */
  let body: React.ReactNode;
  if (state.step === 'experience') {
    body = (
      <ExperienceStep
        onSelect={(experience) => {
          trackSelectExperience(experience);
          dispatch({ type: 'SELECT_EXPERIENCE', experience });
        }}
      />
    );
  } else if (productUnavailable || state.optionsStatus === 'error') {
    body = <WhatsAppFallback contextLabel={expLabel} />;
  } else if (resolvingProduct || loadingOptions) {
    body = <Centered>Loading availability&hellip;</Centered>;
  } else if (state.step === 'success' && state.result) {
    body = (
      <SuccessStep
        result={state.result}
        email={state.contact.email}
        onClose={handleClose}
      />
    );
  } else if (state.step === 'option') {
    body = (
      <OptionStep
        options={state.options}
        selectedOptionId={state.selectedOptionId}
        onSelect={(optionId) => {
          const picked = state.options.find((o) => o.id === optionId);
          if (state.experience && picked) {
            trackSelectOption(state.experience, picked);
          }
          dispatch({ type: 'SELECT_OPTION', optionId });
        }}
      />
    );
  } else if (state.step === 'dateGuests' && option) {
    body = (
      <DateGuestsStep
        option={option}
        slots={monthSlots(state)}
        slotsStatus={monthSlotsStatus(state)}
        months={months}
        selectedMonth={state.selectedMonth}
        onSelectMonth={(month) => dispatch({ type: 'SELECT_MONTH', month })}
        selectedDay={state.selectedDay}
        selectedDate={state.selectedDate}
        selectedSlot={slot}
        quantities={state.quantities}
        experienceLabel={expLabel}
        onSelectDay={(day) => dispatch({ type: 'SELECT_DAY', day })}
        onSelectDate={(date) => {
          const picked = monthSlots(state).find((s) => s.dateTime === date);
          if (state.experience && option && picked) {
            trackSelectDate(state.experience, option, picked, state.quantities);
          }
          dispatch({ type: 'SELECT_DATE', date });
        }}
        onSetQty={(alias, quantity) => dispatch({ type: 'SET_QTY', alias, quantity })}
      />
    );
  } else if (state.step === 'contact') {
    body = (
      <ContactStep
        contact={state.contact}
        comments={state.comments}
        onContact={(patch) => dispatch({ type: 'SET_CONTACT', patch })}
        onComments={(comments) => dispatch({ type: 'SET_COMMENTS', comments })}
      />
    );
  } else if (state.step === 'review' && option && slot) {
    body = (
      <ReviewStep
        experienceTitle={state.experience?.pageTitle ?? ''}
        option={option}
        slot={slot}
        contact={state.contact}
        comments={state.comments}
        quantities={state.quantities}
        methods={ENABLED_PAYMENT_METHODS}
        paymentMethod={state.paymentMethod}
        onPaymentMethod={(paymentMethod) => {
          if (paymentMethod !== state.paymentMethod) {
            trackSelectPaymentMethod(paymentMethod, state.experience ?? undefined);
          }
          dispatch({ type: 'SET_PAYMENT_METHOD', paymentMethod });
        }}
        error={state.error}
      />
    );
  }

  /* ---- Footer ------------------------------------------------------- */
  const blocked =
    productUnavailable ||
    resolvingProduct ||
    loadingOptions ||
    state.optionsStatus === 'error';
  const showFooter =
    !blocked && ['dateGuests', 'contact', 'review'].includes(state.step);

  const backTarget: Step | null =
    state.step === 'dateGuests'
      ? 'option'
      : state.step === 'contact'
        ? 'dateGuests'
        : state.step === 'review'
          ? 'contact'
          : null;

  // The option step can step back to the experience chooser when available.
  const optionBack: Step | null = canPickExperience ? 'experience' : null;

  const payingByCard = state.paymentMethod === 'card';
  const confirmLabel = state.submitting
    ? payingByCard
      ? 'Redirecting…'
      : 'Confirming…'
    : payingByCard
      ? `Pay ${formatPrice(slot ? bookingTotal(slot, state.quantities) : 0)}`
      : 'Confirm booking';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Book a Flying Muse experience"
    >
      <div
        className="absolute inset-0 bg-navy-deep/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-cardHover sm:rounded-2xl">
        {/* Header */}
        <div className="border-b border-charcoal/10 px-6 py-4 sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-sans text-[0.6rem] font-semibold uppercase tracking-label text-crimson">
                {state.experience?.kicker ?? 'Booking'}
              </p>
              <p className="mt-0.5 font-display text-lg font-medium text-charcoal">
                {state.experience?.title ?? 'Book an experience'}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={handleClose}
              className="-mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-charcoal/50 transition-colors hover:bg-charcoal/5 hover:text-charcoal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {state.step !== 'success' && (
            <div className="mt-4">
              <WizardSteps step={state.step} />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-7">{body}</div>

        {/* Footer */}
        {showFooter && (
          <div className="flex items-center justify-between gap-3 border-t border-charcoal/10 bg-cream-soft px-6 py-4 sm:px-7">
            {backTarget ? (
              <button
                type="button"
                onClick={() => dispatch({ type: 'GO', step: backTarget })}
                className="inline-flex items-center gap-1.5 font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/60 transition-colors hover:text-crimson"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : (
              <span />
            )}

            {state.step === 'dateGuests' && (
              <button
                type="button"
                disabled={!canProceedDate}
                onClick={() => {
                  if (state.experience && option && slot) {
                    trackBeginCheckout(
                      state.experience,
                      option,
                      slot,
                      state.quantities,
                    );
                  }
                  dispatch({ type: 'GO', step: 'contact' });
                }}
                className="btn btn-primary px-7 py-2.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continue
              </button>
            )}
            {state.step === 'contact' && (
              <button
                type="button"
                disabled={!contactValid}
                onClick={() => {
                  if (state.experience && option && slot) {
                    trackAddContactInfo(
                      state.experience,
                      option,
                      slot,
                      state.quantities,
                    );
                  }
                  dispatch({ type: 'GO', step: 'review' });
                }}
                className="btn btn-primary px-7 py-2.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Review
              </button>
            )}
            {state.step === 'review' && (
              <button
                type="button"
                disabled={state.submitting}
                onClick={handleConfirm}
                className="btn btn-primary inline-flex items-center gap-2 px-7 py-2.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {state.submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmLabel}
              </button>
            )}
          </div>
        )}

        {/* Option-step back link (no footer on that step) */}
        {!blocked && state.step === 'option' && optionBack && (
          <div className="border-t border-charcoal/10 bg-cream-soft px-6 py-4 sm:px-7">
            <button
              type="button"
              onClick={() => dispatch({ type: 'GO', step: optionBack })}
              className="inline-flex items-center gap-1.5 font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/60 transition-colors hover:text-crimson"
            >
              <ArrowLeft className="h-4 w-4" />
              All experiences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-crimson" />
      <p className="font-sans text-sm font-light text-stone">{children}</p>
    </div>
  );
}
